import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  createPlan, deletePlan, fetchPlans, newPlan, Plan, updatePlan,
} from '../features/plans/plans';
import { RootState } from '../app/store';
import { fetchCampaigns, selectCampaignsByPlanId } from '../features/campaigns/campaigns';
import { CouponOption, planCouponErrors, planPromoCodeErrors } from '../features/plans/planCoupon';
import {
  PLAN_NETWORKS, externalPlanIdsSummary, networkOf, normalisePlan, sameExternalPlanIds, technologiesFor, wholesaleErrors,
} from '../features/plans/planNetwork';
import {
  ChargebeePlanOption, PricingContext, attachedPlan, chargebeeErrors, reprice, settle,
} from '../features/plans/planPricing';
import { Client } from '../http';
import PlanChargebeeField from './PlanChargebeeField';
import PlanCouponField from './PlanCouponField';
import PlanPreviewCard from './PlanPreviewCard';
import PlanPromoCodeField from './PlanPromoCodeField';
import PlanWholesaleIds from './PlanWholesaleIds';

// Human labels for the confirmation diff and validation messages.
const FIELD_LABELS: Record<keyof Plan, string> = {
  id: 'Plan id',
  name: 'Name',
  rsp: 'RSP',
  description1: 'Line under the price',
  description2: 'Internal note',
  speed: 'Speed (Mbps)',
  evening_download_passive: 'Evening download — passive (Mbps)',
  evening_upload_passive: 'Evening upload — passive (Mbps)',
  evening_download_ae: 'Evening download — active ethernet (Mbps)',
  evening_upload_ae: 'Evening upload — active ethernet (Mbps)',
  cost: 'Price shown on the signup site ($ per month)',
  rrp: 'RRP ($ per month)',
  addressLIT: 'Show for LIT',
  addressNonLIT: 'Show for non-LIT',
  addressFTTP: 'Show for FTTP / HFC',
  published: 'Published',
  embedded_coupon: 'Plan coupon',
  apply_embedded_coupon: 'Apply plan coupon',
  promo_code: 'Promo code',
  apply_promo_code: 'Apply promo code',
  promo_cost: 'Price with the promo code ($ per month)',
  network: 'Network',
  download: 'Download (Mbps)',
  upload: 'Upload (Mbps)',
  external_plan_ids: 'Wholesale product ids (layer 3, MEF)',
  layer2_plan_ids: 'Wholesale product ids (layer 2, NPIS)',
};

const formatValue = (v: Plan[keyof Plan]) => {
  if (typeof v === 'object') return externalPlanIdsSummary(v);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v === '') return '(empty)';
  return String(v);
};

interface Change {
  field: keyof Plan;
  from: string;
  to: string;
}

const changed = (before: Plan, after: Plan, field: keyof Plan): boolean => {
  if (field === 'external_plan_ids' || field === 'layer2_plan_ids') {
    return !sameExternalPlanIds(before[field], after[field]);
  }
  return before[field] !== after[field];
};

const diffPlans = (before: Plan, after: Plan): Change[] => (
  (Object.keys(FIELD_LABELS) as (keyof Plan)[])
    .filter((f) => changed(before, after, f))
    .map((f) => ({ field: f, from: formatValue(before[f]), to: formatValue(after[f]) }))
);

const SPEED_FIELDS = [
  'speed',
  'evening_download_passive',
  'evening_upload_passive',
  'evening_download_ae',
  'evening_upload_ae',
] as const;

type SpeedField = (typeof SPEED_FIELDS)[number];

const validate = (plan: Plan, isNew: boolean, pricing: PricingContext): string[] => {
  const errors: string[] = [];
  if (isNew && plan.id.trim() === '') errors.push('Plan id is required');
  if (plan.name.trim() === '') errors.push('Name is required');
  SPEED_FIELDS.forEach((f) => {
    if (!Number.isFinite(plan[f]) || plan[f] < 0) {
      errors.push(`${FIELD_LABELS[f]} must be 0 or more`);
    }
  });
  errors.push(...chargebeeErrors(plan, pricing, isNew));
  errors.push(...planCouponErrors(plan));
  errors.push(...planPromoCodeErrors(plan));
  errors.push(...wholesaleErrors(plan));
  return errors;
};

// PlanCard is the single place where a plan is created or edited. It is
// reached from the plans list (row click → /dashboard/plans/:planId, or
// Add Plan → /dashboard/plans/new) and nothing is persisted until the operator
// reviews the changes in the confirmation dialog and confirms.
const PlanCard = () => {
  const { planId } = useParams();
  const isNew = planId === undefined;
  const decodedId = planId === undefined ? '' : decodeURIComponent(planId);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const plansState = useAppSelector((state: RootState) => state.plans);
  const campaignsByPlanId = useAppSelector(selectCampaignsByPlanId);

  const stored = plansState.value.find((p) => p.id === decodedId);
  const original = useMemo(
    () => (isNew ? newPlan() : stored),
    [isNew, stored],
  );

  const [form, setForm] = useState<Plan | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What the prices are derived from: Chargebee's plans and the coupons.
  const [chargebeePlans, setChargebeePlans] = useState<ChargebeePlanOption[]>([]);
  const [chargebeeLoaded, setChargebeeLoaded] = useState(false);
  const [chargebeeError, setChargebeeError] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Client.get<ChargebeePlanOption[]>('/api/v1/admin/plans/chargebee')
      .then((res) => {
        if (cancelled) return;
        setChargebeePlans(res.data ?? []);
        setChargebeeLoaded(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setChargebeeError(e instanceof Error ? e.message : String(e));
      });
    Client.get<CouponOption[]>('/api/v1/admin/coupons')
      .then((res) => { if (!cancelled) setCoupons(res.data ?? []); })
      .catch(() => { /* PlanCouponField reports it */ });
    return () => { cancelled = true; };
  }, []);

  // Deep links: the store is empty until the list has been visited once.
  useEffect(() => {
    if (plansState.value.length === 0) dispatch(fetchPlans());
    dispatch(fetchCampaigns());
  }, []);

  // (Re)seed the form when the plan arrives or the route changes.
  useEffect(() => {
    setForm(original ? { ...original } : null);
    setSeeded(false);
  }, [original]);

  // Once Chargebee has answered, the derived prices are brought up to date,
  // so the confirmation shows any that no longer match what is stored.
  useEffect(() => {
    if (!form || seeded || !chargebeeLoaded) return;
    setForm(settle(form, { options: chargebeePlans, coupons }));
    setSeeded(true);
  }, [form, seeded, chargebeeLoaded, chargebeePlans, coupons]);

  // The coupons arrive on their own; the displayed price follows them.
  useEffect(() => {
    if (!form || !seeded) return;
    const settled = settle(form, { options: chargebeePlans, coupons });
    if (settled.cost !== form.cost || settled.rrp !== form.rrp || settled.promo_cost !== form.promo_cost) setForm(settled);
  }, [coupons]);

  const campaigns = campaignsByPlanId[decodedId] ?? [];

  if (plansState.status === 'loading' && !form) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', p: 10, mt: 10,
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!original || !form) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3, maxWidth: 900, mx: 'auto', mt: 10,
        }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          Plan &quot;
          {decodedId}
          &quot; was not found.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/plans')}>
          Back to plans
        </Button>
      </Paper>
    );
  }

  const technologyCount = technologiesFor(networkOf(form)).length;
  const changes = diffPlans(original, form);
  const pricing: PricingContext = { options: chargebeePlans, coupons, loaded: chargebeeLoaded };
  const errors = validate(form, isNew, pricing);
  const attached = attachedPlan(form, chargebeePlans);
  const createsChargebeePlan = chargebeeLoaded && !attached && form.rrp.trim() !== '';
  const dirty = isNew || changes.length > 0;

  const setField = <K extends keyof Plan>(field: K, value: Plan[K]) => {
    setForm({ ...form, [field]: value });
  };

  const numberField = (field: SpeedField, label?: string) => (
    <TextField
      key={field}
      label={label ?? FIELD_LABELS[field]}
      type="number"
      value={form[field]}
      onChange={(e) => setField(field, e.target.value === '' ? 0 : Number(e.target.value))}
      inputProps={{ min: 0 }}
      fullWidth
    />
  );

  const eveningRow = (label: string, down: SpeedField, up: SpeedField) => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
      <Typography variant="body2" sx={{ width: { sm: 150 }, flexShrink: 0 }}>
        {label}
      </Typography>
      {numberField(down, 'Download (Mbps)')}
      {numberField(up, 'Upload (Mbps)')}
    </Stack>
  );

  const toggleRow = (
    field: 'addressLIT' | 'addressNonLIT' | 'addressFTTP' | 'published',
    description: string,
  ) => (
    <FormControlLabel
      labelPlacement="start"
      sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
      control={(
        <Switch
          checked={form[field]}
          onChange={(e) => setField(field, e.target.checked)}
          color={field === 'published' ? 'success' : 'primary'}
        />
      )}
      label={(
        <>
          <Typography variant="body2">{FIELD_LABELS[field]}</Typography>
          <Typography variant="caption" color="text.secondary">{description}</Typography>
        </>
      )}
    />
  );

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const toSave = normalisePlan(form);
      if (isNew) {
        await dispatch(createPlan(toSave)).unwrap();
      } else {
        await dispatch(updatePlan(toSave)).unwrap();
      }
      navigate('/dashboard/plans');
    } catch (e) {
      setError(`Failed to save plan: ${e instanceof Error ? e.message : String(e)}`);
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(deletePlan(original)).unwrap();
      navigate('/dashboard/plans');
    } catch (e) {
      setError(`Failed to delete plan: ${e instanceof Error ? e.message : String(e)}`);
      setDeleteOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, maxWidth: 1200, mx: 'auto', mt: 10,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/plans')}>
          Plans
        </Button>
        <Typography variant="h6">
          {isNew ? 'New plan' : original.id}
        </Typography>
        {!isNew && (
          <Chip
            label={original.published ? 'published' : 'draft'}
            size="small"
            color={original.published ? 'success' : 'default'}
          />
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* The signup card on the left stays in view while the form scrolls. */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">
        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 88 },
          }}
        >
          <PlanPreviewCard plan={form} />
        </Box>
        <Stack spacing={2} sx={{ maxWidth: 720, flexGrow: 1, minWidth: 0 }}>
          <TextField
            label={FIELD_LABELS.id}
            value={form.id}
            onChange={(e) => setForm(reprice(form, { id: e.target.value }, pricing))}
            disabled={!isNew}
            helperText={isNew
              ? 'Also the id of the plan in Chargebee, which customers are billed on. Cannot be changed later.'
              : 'Also the id of the plan in Chargebee, which customers are billed on. It cannot be changed.'}
            fullWidth
          />
          <TextField
            label={FIELD_LABELS.name}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            fullWidth
          />
          <TextField
            label={FIELD_LABELS.rsp}
            value={form.rsp}
            onChange={(e) => setField('rsp', e.target.value)}
            fullWidth
          />
          <Divider />

          <PlanChargebeeField
            loading={!chargebeeLoaded && !chargebeeError}
            loadError={chargebeeError}
            attached={attached}
            planId={form.id}
            rrp={form.rrp}
            cost={form.cost}
            onRrp={(rrp) => setForm(reprice(form, { rrp }, pricing))}
          />

          <PlanCouponField
            embeddedCoupon={form.embedded_coupon}
            applyEmbeddedCoupon={form.apply_embedded_coupon}
            onChange={(v) => setForm(reprice(form, v, pricing))}
          />

          <PlanPromoCodeField
            promoCode={form.promo_code}
            applyPromoCode={form.apply_promo_code}
            promoCost={form.promo_cost}
            onChange={(v) => setForm(reprice(form, v, pricing))}
          />

          <TextField
            label={FIELD_LABELS.description1}
            value={form.description1}
            onChange={(e) => setField('description1', e.target.value)}
            helperText={'Shown to customers on the signup plan card, under the price. Filled in from the plan '
            + 'coupon and the RRP until you write your own, for example for an offer whose coupon is not entered yet.'}
            multiline
            fullWidth
          />
          <TextField
            label={FIELD_LABELS.description2}
            value={form.description2}
            onChange={(e) => setField('description2', e.target.value)}
            helperText="Not shown to customers and not sent to Chargebee. Kept for the team's own reference."
            multiline
            fullWidth
          />

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={FIELD_LABELS.network}
              value={networkOf(form)}
              onChange={(e) => setField('network', e.target.value === 'dgtek' ? '' : e.target.value)}
              helperText="Which network the plan is sold on."
              fullWidth
            >
              {PLAN_NETWORKS.map((n) => (
                <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {numberField('speed', 'Speed / download (Mbps)')}
            <TextField
              label={FIELD_LABELS.upload}
              type="number"
              value={form.upload === 0 ? '' : form.upload}
              onChange={(e) => setField('upload', e.target.value === '' ? 0 : Number(e.target.value))}
              inputProps={{ min: 0 }}
              helperText="Only for plans where upload differs, such as 2000/200. Leave empty for a symmetric plan."
              fullWidth
            />
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2">Typical evening speeds</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Shown alongside the headline speed on plan cards. Which pair is
              displayed depends on the building&apos;s access technology.
            </Typography>
            <Stack spacing={2}>
              {eveningRow('Passive (GPON)', 'evening_download_passive', 'evening_upload_passive')}
              {eveningRow('Active ethernet', 'evening_download_ae', 'evening_upload_ae')}
            </Stack>
          </Box>

          {technologyCount > 0 && (
          <>
            <Divider />
            <PlanWholesaleIds
              network={networkOf(form)}
              externalPlanIds={form.external_plan_ids ?? {}}
              layer2PlanIds={form.layer2_plan_ids ?? {}}
              onChange={(ids, layer2) => setForm({ ...form, external_plan_ids: ids, layer2_plan_ids: layer2 })}
            />
          </>
          )}

          <Divider />

          <Stack spacing={1.5}>
            {toggleRow(
              'addressLIT',
              'Offer this plan at addresses in lit buildings — already connected to the network.',
            )}
            {toggleRow(
              'addressNonLIT',
              'Offer this plan at addresses in buildings not yet connected to the network.',
            )}
            {toggleRow(
              'addressFTTP',
              'Offer this plan at addresses served by NBN FTTP or HFC — those addresses '
              + 'see only FTTP / HFC plans.',
            )}
            {toggleRow(
              'published',
              'Only published plans are shown to customers. Unpublished plans are internal drafts.',
            )}
          </Stack>

          {!isNew && campaigns.length > 0 && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Campaigns using this plan</Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {campaigns.map((c) => <Chip key={c.id} label={c.name} size="small" />)}
              </Stack>
            </Box>
          </>
          )}

          {errors.length > 0 && dirty && (
          <Alert severity="warning">
            {errors.join('; ')}
          </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              disabled={!dirty || errors.length > 0 || saving}
              onClick={() => setConfirmOpen(true)}
            >
              {isNew ? 'Create plan…' : 'Save changes…'}
            </Button>
            <Button onClick={() => navigate('/dashboard/plans')}>Cancel</Button>
            <Box sx={{ flexGrow: 1 }} />
            {!isNew && (
            <Button color="error" disabled={saving} onClick={() => setDeleteOpen(true)}>
              Delete…
            </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isNew ? `Create plan "${form.id}"?` : `Apply changes to "${original.id}"?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {createsChargebeePlan
              ? `Chargebee plan "${form.id.trim()}" will be created at $${form.rrp} per month.`
              : `Billed on Chargebee plan "${form.id.trim()}", which is left as it is.`}
            {` The signup site will show $${form.cost} per month`}
            {form.apply_promo_code && form.promo_code && form.promo_cost
              ? `, or $${form.promo_cost} with promo code ${form.promo_code}.`
              : '.'}
          </Typography>
          {isNew ? (
            <Typography variant="body2">
              The plan will be created with the values entered
              {form.published ? ' and will be published immediately.' : ' as an unpublished draft.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.map((c) => (
                  <TableRow key={c.field}>
                    <TableCell>{FIELD_LABELS[c.field]}</TableCell>
                    <TableCell>{c.from}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{c.to}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Back</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)}>
        <DialogTitle>Delete plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`"${original.id}" (${original.name}) will be permanently deleted.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PlanCard;
